import React, { Fragment, useState } from "react";
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { getCountryCodeISO } from "../../utils/general";
import { getNotificationPermission } from "./notificationConstant";
import { selectMenuProps } from "../../utils/selectMenuProps";
import { useDesignTokens } from "../../theme/useDesignTokens";
import FormInput from "../Common/FormInputs/MuiTextField";
import AppCheckbox from "../Common/FormInputs/AppCheckbox";

const selectValuesSx = (theme) => ({ width: "100%", minWidth: 0, [theme.breakpoints.up('md')]: { maxWidth: 280 } });
const formControlSx = (theme) => ({ width: "100%", minWidth: 0, boxSizing: "border-box", [theme.breakpoints.up('md')]: { maxWidth: 280 } });
const userSelectionFieldRowSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "200px minmax(0, 280px)" },
  alignItems: "center",
  gap: 1.5,
  marginBottom: 2,
  "&:last-of-type": { marginBottom: 0 },
};
const userSelectionBlockSx = {
  "& > div": { marginBottom: 2 },
  "& > div:last-of-type": { marginBottom: 0 },
};
const sectionCardSx = {
  p: 2,
  mt: 1.5,
  boxShadow: 2,
  borderRadius: 1,
};

const UserSelection = ({
    allowedDeviceCategories,
    allowedCompanyBrand,
    allowedCountries = [],
    setAlert,
    country,
    deviceOwnership,
    userStartLastLogin,
    userEndLastLogin,
    handleUserCountries,
    handleUserDeviceOwnership,
    handleUserStartLastLogin,
    handleUserEndLastLogin,
    uploadedUserIdFileInput,
    userIdCSVFileInputRef,
    handleUserIdFileInput,
    userLevel,
    broadcastNotification,
    handleBroadcastNotification
}) => {

    const theme = useTheme()
    const { colors } = useDesignTokens();
    const primaryColor = colors?.primary?.main || theme.palette.primary.main;
    const focusOutlineSx = {
      "& .MuiOutlinedInput-notchedOutline": {
        border: "1px solid rgba(0, 0, 0, 0.42) !important",
      },
      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: `${primaryColor} !important`,
      },
      "& .MuiOutlinedInput-root.Mui-expanded .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(0, 0, 0, 0.42) !important",
        borderWidth: "1px",
      },
      "& .MuiInputLabel-root.Mui-focused, & .MuiInputLabel-root.Mui-expanded": {
        color: `${primaryColor} !important`,
      },
    };
    const [individualUsers, setIndividualUsers] = useState(false)

    const menuProps = selectMenuProps;

    const allDeviceOwnershipSelected = allowedDeviceCategories.length === deviceOwnership.length

    const filter = createFilterOptions();

    const handleUploadedDeviceOwnership = (event, selectedOptions, reason, details) => {
        const selectedOption = details?.option;
        const cleanedOptions = selectedOptions.filter((option) => option !== "Select All");

        if (selectedOption === "Select All") {
            if (!allDeviceOwnershipSelected) {
                handleUserDeviceOwnership([...allowedDeviceCategories]);
            } else {
                handleUserDeviceOwnership([]);
            }
            return;
        }

        if (reason === "clear") {
            handleUserDeviceOwnership([]);
            return;
        }

        handleUserDeviceOwnership(cleanedOptions);
    };

    const handleUserDeviceOwnershipRenderer = (props, option, state) => {
        const selected = state?.selected ?? false;
        const selectAllProps =
            option === "Select All"
                ? { checked: allDeviceOwnershipSelected }
                : {};
        return (
            <li {...props} key={props.key}>
                <AppCheckbox
                    checked={selected}
                    color="primary"
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    checkboxSx={{ mr: 1 }}
                    {...selectAllProps}
                />
                {option != null ? option.toString() : "Select All"}
            </li>
        );
    };


    return (
        <Fragment>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>
              Users
            </Typography>
            <Paper sx={sectionCardSx}>
                <Box sx={userSelectionBlockSx}>
                    {!broadcastNotification && (
                            <Box sx={userSelectionFieldRowSx}>
                                <Typography variant="body2" fontWeight={600}>Upload User Ids CSV File</Typography>
                                <Box sx={formControlSx(theme)}>
                                    <input
                                        accept=".csv"
                                        type="file"
                                        {...(() => {
                                            if (uploadedUserIdFileInput === "") {
                                                return { value: "" }
                                            }
                                            return {}
                                        })()}
                                        style={{
                                            width: "100%",
                                            boxSizing: "border-box",
                                            padding: 8,
                                            border: "1px solid rgba(0,0,0,0.42)",
                                            borderRadius: 4,
                                        }}
                                        name={uploadedUserIdFileInput?.name ?? uploadedUserIdFileInput}
                                        onChange={(e) => {
                                            handleUserIdFileInput(e.target.files[0])
                                            setIndividualUsers(!individualUsers)
                                        }}
                                        ref={userIdCSVFileInputRef}
                                    />
                                </Box>
                            </Box>
                    )}
                    {!individualUsers && getNotificationPermission([], "userCountrySelection", 'level', userLevel) && (
                        <Box sx={userSelectionFieldRowSx}>
                            <Typography variant="body2" fontWeight={600}>Broadcast Notification</Typography>
                            <Box>
                                <AppCheckbox
                                    checked={broadcastNotification}
                                    onChange={(e) => {
                                        handleBroadcastNotification(e.target.checked)
                                    }}
                                    color="default"
                                    label={"Broadcast Notification"}
                                    sx={{ ml: "-11px" }}
                                />
                            </Box>
                        </Box>
                    )}
                    {broadcastNotification && getNotificationPermission([], "userCountrySelection", 'level', userLevel) && (
                        <Box sx={userSelectionFieldRowSx}>
                            <Typography variant="body2" fontWeight={600}>Country</Typography>
                            <Box sx={formControlSx(theme)} style={{ width: "100%" }}>
                                <FormInput
                                  id="select-outlined-country-name-id"
                                  type="select"
                                  size="small"
                                  fullWidth
                                  name="country"
                                  value={country}
                                  onChange={(e) => {
                                    handleUserCountries(e.target.value)
                                  }}
                                  displayEmpty
                                  renderValue={(v) => (!v ? "Select Country" : v)}
                                  MenuProps={menuProps}
                                  options={[
                                    { value: "None", label: "None" },
                                    ...(allowedCountries
                                      ?.filter((country) => getCountryCodeISO(country.toUpperCase()))
                                      .map((country) => ({
                                        value: getCountryCodeISO(country.toUpperCase()),
                                        label: getCountryCodeISO(country.toUpperCase()),
                                      })) || []),
                                  ]}
                                  sx={{ ...selectValuesSx(theme), ...focusOutlineSx }}
                                />
                            </Box>
                        </Box>
                    )}

                    {!broadcastNotification && getNotificationPermission([], "userCategorySelection", 'level', userLevel) && (
                            <Box sx={userSelectionFieldRowSx}>
                                <Typography variant="body2" fontWeight={600}>Device Ownership:</Typography>
                                <Box sx={formControlSx(theme)} style={{ width: '100%' }}>
                                    <Autocomplete
                                        multiple
                                        size="small"
                                        limitTags={10}
                                        options={allowedDeviceCategories}
                                        value={deviceOwnership}
                                        disableCloseOnSelect
                                        getOptionLabel={(option) => option != null ? String(option) : ''}
                                        renderOption={handleUserDeviceOwnershipRenderer}
                                        onChange={handleUploadedDeviceOwnership}
                                        renderInput={(params) => (
                                            <TextField
                                              {...params}
                                              variant="outlined"
                                              placeholder={deviceOwnership?.length ? "" : "Select Device Ownerships"}
                                              fullWidth
                                              sx={{
                                                ...focusOutlineSx,
                                                "& .MuiOutlinedInput-root": {
                                                  height: "auto",
                                                  minHeight: 45,
                                                  alignItems: "center",
                                                  paddingTop: "0 !important",
                                                  paddingBottom: "0 !important",
                                                },
                                                "& .MuiAutocomplete-inputRoot": {
                                                  minHeight: 45,
                                                  alignItems: "center",
                                                  flexWrap: "wrap",
                                                  padding: "0 39px 0 14px !important",
                                                },
                                                "& .MuiAutocomplete-tag": {
                                                  margin: "3px !important",
                                                  maxWidth: "calc(100% - 6px)",
                                                },
                                                "& .MuiInputBase-input::placeholder": {
                                                  color: "rgba(0, 0, 0, 0.6)",
                                                  opacity: 1,
                                                },
                                                "& .MuiAutocomplete-endAdornment": {
                                                  top: "50%",
                                                  transform: "translateY(-50%)",
                                                },
                                                "& .MuiInputBase-input": {
                                                  borderBottom: "none !important",
                                                  boxShadow: "none !important",
                                                  margin: "0 !important",
                                                  padding: "0 !important",
                                                },
                                                "& .MuiInputBase-input:focus": {
                                                  borderBottom: "none !important",
                                                  boxShadow: "none !important",
                                                },
                                              }}
                                            />
                                        )}
                                        filterOptions={(options, params) => {
                                            const filtered = filter(options, params);
                                            return ["Select All", ...filtered];
                                        }}
                                        isOptionEqualToValue={(option, value) => option === value}
                                        ListboxProps={{ style: { maxHeight: 250 } }}
                                        style={{ width: "100%", minWidth: 0 }}
                                    />
                                </Box>
                            </Box>
                    )}
                    {/* will Implement last login feature for notification later on */}
                    {/* {getNotificationPermission([], "userLoginSelection", 'level', userLevel) && <Grid container style={{ marginBottom: "20px" }}>
                        <Grid item xs={2} style={{ marginLeft: "10px" }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>Last Login: </Typography>
                        </Grid>
                        <Grid item xs={3} >
                            <DatePicker
                                selected={userStartLastLogin}
                                maxDate={new Date()}
                                onChange={(date) => {
                                    if (date) {
                                        handleUserStartLastLogin(date.getTime())
                                    }
                                    else {
                                        handleUserStartLastLogin(date)
                                    }
                                }}
                                placeholderText="Start Login Date"
                                dateFormat='MM/dd/yyyy'
                            />
                        </Grid>
                        <Grid item xs={3} >
                            <DatePicker
                                selected={userEndLastLogin}
                                maxDate={new Date()}
                                onChange={(date) => {
                                    if (date) {
                                        handleUserEndLastLogin(date.getTime())
                                    }
                                    else {
                                        handleUserEndLastLogin(date)
                                    }
                                }}
                                placeholderText="End Login Date"
                                dateFormat='MM/dd/yyyy'
                            />
                        </Grid>
                    </Grid>} */}
                </Box>
            </Paper>
        </Fragment>
    );
}


export default UserSelection